import React, { Component } from "react";
import EmptyState from "../../common/EmptyState";
import Breadcrumb from "../../common/breadcrumb";
import { connect } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TrendingUp, RefreshCw, AlertTriangle, CheckCircle } from "react-feather";
import { getAllProductStatsRedux, backfillProductStatsRedux } from "../../../actions";
import { getSingleProduct } from "../../../firebase/firebase.utils";
import { Link } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { confirmDialog } from "../../common/ConfirmDialog";

class RestockDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      backfilling: false,
      enriched: [],
      lowStockThreshold: 5,
    };
  }

  async componentDidMount() {
    await this.props.getAllProductStatsRedux();
    await this.enrichStats();
  }

  enrichStats = async () => {
    const { productStats } = this.props;
    if (!productStats || productStats.length === 0) {
      this.setState({ loading: false });
      return;
    }

    const enrichedWithDeleted = await Promise.all(
      productStats.slice(0, 50).map(async (stat) => {
        try {
          const product = await getSingleProduct(stat.productId);
          if (!product) return { ...stat, name: "Deleted product", inventory: 0, pictures: [], deleted: true };
          const inventory = product.displayedVariations && product.displayedVariations.length > 0
            ? product.displayedVariations.reduce((sum, v) => sum + (parseInt(v.inventory) || 0), 0)
            : parseInt(product.inventory) || 0;
          return {
            ...stat,
            name: product.name,
            pictures: product.pictures || [],
            inventory,
            productSlug: product.id,
          };
        } catch {
          return { ...stat, name: "Unknown", inventory: 0, pictures: [] };
        }
      })
    );

    // Deleted products still have historical sales stats, but shouldn't
    // linger in the restock dashboard once the product itself is gone.
    const enriched = enrichedWithDeleted.filter((p) => !p.deleted);
    this.setState({ enriched, loading: false });
  };

  handleBackfill = async () => {
    if (!(await confirmDialog(
      "This will rebuild all product stats from scratch by reading every transaction. It may take a moment. Continue?",
      { danger: false, confirmText: "Continue" }
    ))) return;
    this.setState({ backfilling: true });
    try {
      await this.props.backfillProductStatsRedux();
      toast.success("Product stats rebuilt successfully.");
      await this.props.getAllProductStatsRedux();
      await this.enrichStats();
    } catch (e) {
      toast.error("Backfill failed: " + e.message);
    }
    this.setState({ backfilling: false });
  };

  render() {
    const { loading, backfilling, enriched, lowStockThreshold } = this.state;
    const { currentAdmin } = this.props;
    const isSuper = currentAdmin && currentAdmin.status === "Admin";

    const urgent = enriched.filter((p) => p.inventory <= 0);
    const low = enriched.filter((p) => p.inventory > 0 && p.inventory <= lowStockThreshold);
    const healthy = enriched.filter((p) => p.inventory > lowStockThreshold);

    return (
      <div>
        <ToastContainer />
        <Breadcrumb title="Restock Dashboard" parent="Products" />
        <div className="container-fluid kd-cash kd-prod kd-st--mint">
          {/* Header row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 20,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                Top-selling products ordered by all-time units sold. Stats update automatically on every sale or cancellation.
              </div>
            </div>
            {isSuper && (
              <button
                className="btn"
                style={{
                  background: "var(--kd-ink)",
                  color: "white",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
                onClick={this.handleBackfill}
                disabled={backfilling}
              >
                {backfilling ? (
                  <ClipLoader size={14} color="white" loading />
                ) : (
                  <RefreshCw size={14} />
                )}
                {backfilling ? "Rebuilding…" : "Rebuild Stats from History"}
              </button>
            )}
          </div>

          {/* Summary cards */}
          {!loading && enriched.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 14,
                marginBottom: 24,
                flexWrap: "wrap",
              }}
            >
              {[
                {
                  label: "Out of Stock",
                  value: urgent.length,
                  icon: <AlertTriangle size={18} />,
                  color: "#DC2626",
                  bg: "#FEF2F2",
                  border: "#FCA5A5",
                },
                {
                  label: `Low Stock (≤ ${lowStockThreshold} pc)`,
                  value: low.length,
                  icon: <AlertTriangle size={18} />,
                  color: "#D97706",
                  bg: "#FFFBEB",
                  border: "#FCD34D",
                },
                {
                  label: "Healthy Stock",
                  value: healthy.length,
                  icon: <CheckCircle size={18} />,
                  color: "#059669",
                  bg: "#ECFDF5",
                  border: "#6EE7B7",
                },
                {
                  label: "Top Seller Units",
                  value: enriched[0] ? `${enriched[0].totalSold} pc` : "—",
                  icon: <TrendingUp size={18} />,
                  color: "var(--kd-lilac-deep)",
                  bg: "#EEF2FF",
                  border: "#A5B4FC",
                },
              ].map((card) => (
                <div
                  key={card.label}
                  style={{
                    background: card.bg,
                    border: `1.5px solid ${card.border}`,
                    borderRadius: 10,
                    padding: "14px 20px",
                    minWidth: 160,
                    flex: "0 0 auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: card.color,
                      marginBottom: 6,
                    }}
                  >
                    {card.icon}
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {card.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: card.color }}>
                    {card.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table */}
          <div className="card" style={{ borderRadius: 10, overflow: "hidden" }}>
            {loading ? (
              <div className="kd-empty-state" role="status">
                <span className="kd-hop" aria-hidden="true"><i /><i /><i /></span>
                <p>Loading product stats…</p>
              </div>
            ) : enriched.length === 0 ? (
              <EmptyState
                hint={
                  isSuper && (
                    <button type="button" className="kd-chip-link kd-st--sky" onClick={this.handleBackfill}>
                      Rebuild from transaction history
                    </button>
                  )
                }
              >
                No sales stats yet.
              </EmptyState>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table table-hover" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      {["#", "Product", "Total Sold", "Total Revenue", "Current Stock", "Status", "Action"].map((h) => (
                        <th
                          key={h}
                          style={{
                            background: "var(--kd-ink)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: 12,
                            padding: "12px 14px",
                            whiteSpace: "nowrap",
                            border: "none",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enriched.map((p, i) => {
                      const isOut = p.inventory <= 0;
                      const isLow = p.inventory > 0 && p.inventory <= lowStockThreshold;

                      return (
                        <tr
                          key={p.productId}
                          style={{ background: isOut ? "#FEF2F2" : isLow ? "#FFFBEB" : "#fff" }}
                        >
                          <td style={{ fontSize: 13, color: "#888", padding: "12px 14px", width: 40 }}>
                            {i + 1}
                          </td>
                          <td style={{ padding: "10px 14px", minWidth: 220 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              {p.pictures && p.pictures[0] ? (
                                <img
                                  src={p.pictures[0]}
                                  alt=""
                                  style={{ width: 42, height: 42, borderRadius: 6, objectFit: "cover", border: "1px solid #e0e0e0", flexShrink: 0 }}
                                />
                              ) : (
                                <div style={{ width: 42, height: 42, borderRadius: 6, background: "#f0f0f0", flexShrink: 0 }} />
                              )}
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  maxWidth: 200,
                                }}
                              >
                                {p.name}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px", fontWeight: 700, fontSize: 14, color: "var(--kd-lilac-deep)", whiteSpace: "nowrap" }}>
                            {p.totalSold > 0 ? `${p.totalSold} pc` : "—"}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: 13, color: "#059669", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {p.totalRevenue > 0 ? `৳ ${p.totalRevenue.toLocaleString()}` : "—"}
                          </td>
                          <td style={{ padding: "12px 14px", fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", color: isOut ? "#DC2626" : isLow ? "#D97706" : "#1e293b" }}>
                            {p.inventory} pc
                          </td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                            {isOut ? (
                              <span style={{ background: "#DC2626", color: "white", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                                Out of Stock
                              </span>
                            ) : isLow ? (
                              <span style={{ background: "#D97706", color: "white", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                                Low Stock
                              </span>
                            ) : (
                              <span style={{ background: "#059669", color: "white", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                                In Stock
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                            {p.deleted ? (
                              <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>Deleted</span>
                            ) : (
                              <Link
                                to={`/products/physical/add-product/${p.productId}`}
                                className="btn btn-sm"
                                style={{
                                  background: "var(--kd-ink)",
                                  color: "white",
                                  fontSize: 12,
                                  padding: "4px 12px",
                                }}
                              >
                                Restock
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

              </div>
            )}
          </div>

          {/* Low stock threshold control */}
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>Low stock threshold:</span>
            {[3, 5, 10, 20].map((n) => (
              <button
                key={n}
                onClick={() => this.setState({ lowStockThreshold: n })}
                className="btn btn-sm"
                style={{
                  background: lowStockThreshold === n ? "var(--kd-ink)" : "#f1f5f9",
                  color: lowStockThreshold === n ? "white" : "#475569",
                  border: "none",
                  fontSize: 12,
                  padding: "3px 10px",
                  borderRadius: 6,
                }}
              >
                {n} pc
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  productStats: state.products.productStats || [],
  currentAdmin: state.admins.currentAdmin,
});

export default connect(mapStateToProps, {
  getAllProductStatsRedux,
  backfillProductStatsRedux,
})(RestockDashboard);
